import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { lazily } from 'react-lazily';
import { useSearchParams } from 'react-router-dom';

import { ErrorBoundaryForPages } from '@hotjar/error-handler';
import { PageLoader } from '@hotjar/webapp/components/PageLoader/PageLoader';

import { upsell } from '@hotjar/webapp/domains/revenue/upsell';

import { useCurrentSite } from '@hotjar/site';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';

import { noop } from '@hotjar/webapp/lib/function';

import type { GlobalFilter } from '@hotjar/global-filters';
import {
  getDefaultFilterInUiFormat,
  readFiltersFromUrl,
  convertFromNewUiToQueryFormat,
} from '@hotjar/global-filters';
import { isAllowed } from '@hotjar/webapp/domains/common/feature';
import type { AdditionalTrackingData } from '@hotjar/dashboard-quantz';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { navigateToInstaller } from '@hotjar/webapp/domains/activation/routing';
import { useTargetingSettings } from '@hotjar/webapp/hooks/useTargetingSettings';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { useAccountTrialInfo } from '@hotjar/account';
import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';
import { TrafficAndCoverageBanner } from '@hotjar/webapp/components/TrafficAndCoverageBanner/TrafficAndCoverageBanner';
import { TRAFFIC_UPSELL_BANNER } from '@hotjar/upsell';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { useTranslation } from '@hotjar/translations';
import { trackDashboardViewed, trackWidgetViewed, trackDashboardUpdated } from './trackMethods';
import { DashboardFilterChain } from './DashboardFilterChain';
import { trackAllWidgetLoaded, trackPageStarting } from './perfMethods';
import { getFeedbackEmotionIcon } from './getFeedbackEmotionIcon';

// We use lazily lib to be able to import component without a default export.
const { DashboardPage } = lazily(() => import('@hotjar/dashboard-quantz'));

export const AsyncDashboardQuantzPage = (): JSX.Element => {
  const { t } = useTranslation('dashboard-quantz');
  usePageTitle(`${t('DashboardPage.title')} - Hotjar`);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  const [currentFiltersInUiFormat, setCurrentFiltersInUiFormat] = useState<GlobalFilter>(
    getDefaultFilterInUiFormat(),
  );
  const { data: site } = useCurrentSite();
  const { sessionCaptureConsoleConsent } = useTargetingSettings(site);
  const trackingCodeState = useTrackingCodeState();
  const hasDashboardUpdatePermission = usePermission('dashboard-update');
  const hasSessionSettingsUpdatePermission = usePermission('sites-update');

  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const trialInfo = useAccountTrialInfo();
  const isNewScaleEnabled = useFeatureFlag('billing_new_scale');
  const isSiteInScaleTrialCohort =
    useFeatureFlag('scale_trial_cohort') && site?.effectivePlan === 'business';
  const isScaleTrial =
    (isNewScaleEnabled && isSiteInScaleTrialCohort) ||
    (!isNewScaleEnabled &&
      !isOnActiveOrEligibleTrial(trialInfo) &&
      site?.effectivePlan === 'business');
  const isScaleOrScaleTrial =
    site &&
    (trialInfo || undefined) &&
    isSiteOnScaleOrScaleTrial({ ...site, trial: { active: isOnActiveOrEligibleTrial(trialInfo) } });
  const { click, getPermissionLevelForSite, viewElement } = useTrack();
  const { isLoading: trafficCoverageLoading, coveragePercentage } = useTrafficCoverage();

  const setFiltersFromUrl = () => {
    let filtersFromUrl;
    try {
      filtersFromUrl = readFiltersFromUrl([]).filtersInUiFormat;
    } catch {
      filtersFromUrl = getDefaultFilterInUiFormat();
    }
    setCurrentFiltersInUiFormat(filtersFromUrl);
  };

  useEffect(() => {
    trackPageStarting();
    setFiltersFromUrl();
  }, []);

  const openUpsellModal = useCallback(
    // FIXME: referrer should be of KnownReferrer type
    (referrer: string, additionalTrackingData?: AdditionalTrackingData) => {
      if (site != null) {
        upsell({
          site,
          referrer: referrer as KnownReferrer, // FIXME: remove type assertion
          additionalTrackingData: {
            state_event_was_triggered_on: 'dashboard',
            ...additionalTrackingData,
          },
        }).then(noop, noop);
      }
    },
    [site],
  );

  const segmentData = useMemo(
    () => ({
      account_id: site?.account.id,
      site_id: site?.id,
      organization_id: site?.organization.id,
      permission_level: site ? getPermissionLevelForSite(site) || '' : '',
      shown_traffic_coverage: coveragePercentage,
      state_event_was_triggered_on: 'dashboard',
    }),
    [coveragePercentage, getPermissionLevelForSite, site],
  );

  const trackClick = useCallback(
    (eventName: string, uiPosition: string, additionalData: Record<string, string>) =>
      click(eventName, uiPosition, {
        ...segmentData,
        ...additionalData,
      }),
    [click, segmentData],
  );

  const trackViewElement = useCallback(
    (eventName: string, uiPosition: string, additionalData: Record<string, string>) =>
      viewElement(eventName, uiPosition, {
        ...segmentData,
        ...additionalData,
      }),
    [segmentData, viewElement],
  );

  if (!site || !currentFiltersInUiFormat || trafficCoverageLoading) {
    return <PageLoader />;
  }

  const onFiltersChange = (filtersInUiFormat: GlobalFilter) => {
    setCurrentFiltersInUiFormat(filtersInUiFormat);

    const queryString = JSON.stringify(convertFromNewUiToQueryFormat(filtersInUiFormat));
    setSearchParams({ filters: queryString });
  };

  const goToSetupPage = () => {
    trackClick('Go to setup page', 'Dashboard Quantz', {});
    navigateToInstaller(site);
  };

  const filterChainComponent = (
    <DashboardFilterChain
      currentFiltersInUiFormat={currentFiltersInUiFormat}
      onFiltersChange={onFiltersChange}
      onUpsellRequest={openUpsellModal}
    />
  );

  const bannerComponent = (
    <TrafficAndCoverageBanner
      type={TRAFFIC_UPSELL_BANNER.DASHBOARD}
      track={{ page: 'dashboard', trackViewElement, trackClick }}
    />
  );

  return (
    <ErrorBoundaryForPages squadSentryTag="product-foundations" featureName="dashboard">
      <Suspense fallback={<PageLoader />}>
        <DashboardPage
          siteId={site.id}
          currentSite={site}
          isTrackingCodeInstalled={!!trackingCodeState?.installed}
          sessionFilters={currentFiltersInUiFormat}
          filterChainComponent={filterChainComponent}
          bannerComponent={bannerComponent}
          goToSetupPage={goToSetupPage}
          // TODO: Remove openUpsellModal and use upsell function instead
          openUpsellModal={openUpsellModal}
          trackDashboardViewed={(config) =>
            trackDashboardViewed({ site }, config, currentFiltersInUiFormat)
          }
          trackWidgetViewed={(config, additionalData) =>
            trackWidgetViewed(
              { site },
              config,
              currentFiltersInUiFormat,
              // We need to cast it to unknown first to avoid:
              // "Conversion of type 'AdditionalTrackingData' to type 'Record' may be a mistake because neither type sufficiently overlaps with the other"
              additionalData as unknown as Record<string, string>,
            )
          }
          trackDashboardUpdated={(config, appliedActions) =>
            trackDashboardUpdated({ site }, config, appliedActions)
          }
          trackClick={(eventName, additionalData) => {
            // eslint-disable-next-line camelcase
            const { ui_position, ...rest } = additionalData;
            trackClick(eventName, ui_position, rest as Record<string, string>);
          }}
          trackViewElement={(eventName, additionalData) => {
            // eslint-disable-next-line camelcase
            const { ui_position, ...rest } = additionalData;
            trackViewElement(eventName, ui_position, rest as Record<string, string>);
          }}
          trackAllWidgetLoaded={trackAllWidgetLoaded}
          isAllowed={isAllowed}
          getFeedbackEmotionIcon={getFeedbackEmotionIcon}
          hasDashboardUpdatePermission={hasDashboardUpdatePermission}
          isScaleOrScaleTrial={isScaleOrScaleTrial}
          isScaleTrial={isScaleTrial}
          sessionCaptureConsoleConsent={sessionCaptureConsoleConsent}
          hasSessionSettingsUpdatePermission={hasSessionSettingsUpdatePermission}
        />
      </Suspense>
    </ErrorBoundaryForPages>
  );
};
